import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, CalendarPlus, Printer, Phone, XCircle, Check, Clock, Ban, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Container, Reveal } from "@/components/common/Primitives";
import { Kicker, Headline, Lead, Field, INPUT_CLS } from "@/components/common/Editorial";
import { PHONE } from "@/components/layout/Header";
import { api, errorCode } from "@/lib/api";
import { useLang, errorText } from "@/lib/i18n";
import { useContent } from "@/lib/content";
import { usePageMeta } from "@/lib/seo";
import { fmtDateTime } from "@/lib/format";
import { downloadIcs } from "@/lib/booking";
import { BookingDetails } from "./Booking";

const STATUS_LABEL = {
  pending: { nn: "Motteken", en: "Received", dot: "bg-brass" },
  confirmed: { nn: "Stadfesta", en: "Confirmed", dot: "bg-success" },
  declined: { nn: "Avslått", en: "Declined", dot: "bg-destructive" },
  cancelled: { nn: "Avbestilt", en: "Cancelled", dot: "bg-muted-foreground" },
};

/** Status as a quiet caps label with a coloured dot – no pill. */
const StatusLabel = ({ status, lang, testid }) => {
  const st = STATUS_LABEL[status] || STATUS_LABEL.pending;
  return (
    <span className="inline-flex items-center gap-2.5" data-testid={testid}>
      <span className={`h-2 w-2 rounded-full ${st.dot}`} aria-hidden />
      <span className="label-caps text-foreground">{lang === "en" ? st.en : st.nn}</span>
    </span>
  );
};

const Timeline = ({ b }) => {
  const { lang, tx } = useLang();
  const hist = b.status_history || [];
  const icon = (s) => (s === "confirmed" ? Check : s === "declined" ? Ban : s === "cancelled" ? XCircle : Clock);
  const label = (h) => {
    const base = lang === "en" ? STATUS_LABEL[h.status]?.en : STATUS_LABEL[h.status]?.nn;
    const by = h.by === "guest" ? tx(" av deg", " by you") : h.by === "admin" ? tx(" av hotellet", " by the hotel") : h.by === "auto" ? tx(" automatisk", " automatically") : "";
    return `${base}${by}${h.late ? tx(" (etter fristen)", " (after deadline)") : ""}`;
  };
  return (
    <ol className="divide-y divide-border border-y border-border" data-testid="my-booking-status-timeline">
      {hist.map((h, i) => {
        const I = icon(h.status);
        const last = i === hist.length - 1;
        return (
          <li key={i} className="flex items-start gap-4 py-3.5">
            <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${last ? "bg-foreground text-background" : "border border-border text-muted-foreground"}`}><I className="h-3 w-3" strokeWidth={2} /></span>
            <span className="min-w-0">
              <p className={`text-[0.9375rem] ${last ? "font-medium text-foreground" : "text-foreground/80"}`}>{label(h)}</p>
              <p className="text-xs text-muted-foreground tabular">{fmtDateTime(h.at, lang)}</p>
            </span>
          </li>
        );
      })}
      {b.status === "pending" && (
        <li className="flex items-start gap-4 py-3.5">
          <span className="mt-0.5 h-6 w-6 shrink-0 rounded-md border border-dashed border-border" aria-hidden />
          <p className="text-[0.9375rem] text-muted-foreground">{tx("Ventar på stadfesting frå hotellet", "Awaiting confirmation from the hotel")}</p>
        </li>
      )}
    </ol>
  );
};

export default function MyBooking() {
  const [sp] = useSearchParams();
  const { lang, tx } = useLang();
  const { data: content } = useContent();
  usePageMeta(tx("Mi bestilling", "My booking"), tx("Sjå, endre eller avbestill opphaldet ditt på Rosendal Fjordhotel.", "View, change or cancel your stay at Rosendal Fjordhotel."));
  const byId = Object.fromEntries((content?.room_types || []).map((r) => [r.id, r]));
  const [ref, setRef] = useState(sp.get("ref") || "");
  const [email, setEmail] = useState(sp.get("email") || "");
  const [booking, setBooking] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const lookup = useMutation({
    mutationFn: async () => (await api.post("/bookings/lookup", { ref: ref.trim().toUpperCase(), email: email.trim() })).data,
    onSuccess: (b) => setBooking(b),
    onError: (err) => { setBooking(null); toast.error(errorText(errorCode(err), lang)); },
  });
  const cancel = useMutation({
    mutationFn: async () => (await api.post("/bookings/cancel", { ref: booking.ref, email: booking.guest.email })).data,
    onSuccess: (b) => { setBooking(b); setConfirmOpen(false); toast.success(tx("Bestillinga er avbestilt. Me har sendt deg ei stadfesting.", "Your booking is cancelled. We've emailed you a confirmation.")); },
    onError: (err) => toast.error(errorText(errorCode(err), lang)),
  });

  useEffect(() => {
    if (sp.get("ref") && sp.get("email") && !booking && !lookup.isPending) lookup.mutate();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const roomNames = Object.fromEntries(Object.values(byId).map((r) => [r.id, r.name?.[lang] || r.name?.nn]));

  return (
    <div className="pb-24 pt-10 lg:pb-32 lg:pt-16" data-testid="my-booking-page">
      <Container>
        <Reveal className="max-w-2xl">
          <Kicker className="mb-5">{tx("Mi bestilling", "My booking")}</Kicker>
          <Headline as="h1">{tx("Finn opphaldet ditt", "Find your stay")}</Headline>
          <Lead>{tx("Skriv inn bookingreferansen (RFH-…) frå stadfestinga og e-postadressa du bestilte med.", "Enter the booking reference (RFH-…) from your confirmation and the email address you booked with.")}</Lead>
        </Reveal>

        <Reveal delay={0.05} className="mt-12 border-t border-border pt-8 no-print">
          <form onSubmit={(e) => { e.preventDefault(); lookup.mutate(); }} className="grid gap-5 sm:grid-cols-[1fr_1.4fr_auto] sm:items-end" data-testid="my-booking-lookup-form">
            <Field id="lookup-ref" label={tx("Bookingreferanse", "Booking reference")}>
              <Input id="lookup-ref" value={ref} onChange={(e) => setRef(e.target.value.toUpperCase())} placeholder="RFH-XXXXX" className={`${INPUT_CLS} uppercase tabular`} required data-testid="my-booking-ref-input" />
            </Field>
            <Field id="lookup-email" label={tx("E-postadresse", "Email address")}>
              <Input id="lookup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={INPUT_CLS} required autoComplete="email" data-testid="my-booking-email-input" />
            </Field>
            <Button type="submit" size="lg" className="h-12" disabled={lookup.isPending} data-testid="my-booking-lookup-submit">{lookup.isPending ? tx("Søkjer …", "Searching …") : tx("Finn bestilling", "Find booking")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Button>
          </form>
        </Reveal>

        {lookup.isError && !booking && (
          <div className="mt-8 max-w-2xl border-t border-foreground pt-5" data-testid="my-booking-not-found">
            <p className="text-[1.0625rem] font-medium text-foreground">{errorText(errorCode(lookup.error), lang)}</p>
            <p className="mt-2 text-[0.9375rem] leading-relaxed text-muted-foreground">{tx("Sjekk at referansen og e-postadressa er dei same som i stadfestinga. Finn du ho ikkje? Ring oss på", "Check that the reference and email match your confirmation. Can't find it? Call us on")} <a href={PHONE.tel} className="font-medium text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground tabular">{PHONE.display}</a>.</p>
          </div>
        )}

        {booking && (
          <div className="mt-16 grid gap-12 lg:grid-cols-12 lg:gap-16" data-testid="my-booking-result">
            <div className="lg:col-span-7">
              <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-6">
                <div>
                  <Kicker>{tx("Bookingreferanse", "Booking reference")}</Kicker>
                  <p className="mt-2 text-[2rem] font-medium leading-none tracking-[-0.02em] text-foreground tabular" data-testid="my-booking-reference">{booking.ref}</p>
                </div>
                <StatusLabel status={booking.status} lang={lang} testid="my-booking-status" />
              </div>
              {booking.admin_message && (
                <div className="mt-6 border-l-2 border-foreground pl-5" data-testid="my-booking-admin-message">
                  <Kicker>{tx("Melding frå hotellet", "Message from the hotel")}</Kicker>
                  <p className="mt-2 text-[0.9375rem] leading-relaxed text-foreground">{booking.admin_message}</p>
                </div>
              )}
              <div className="mt-8">
                <BookingDetails b={booking} byId={byId} lang={lang} />
              </div>
            </div>
            <aside className="space-y-12 lg:col-span-4 lg:col-start-9">
              <div>
                <Kicker className="mb-4">{tx("Status", "Status")}</Kicker>
                <Timeline b={booking} />
              </div>
              <div className="no-print">
                <Kicker className="mb-4">{tx("Handlingar", "Actions")}</Kicker>
                <div className="grid gap-2">
                  <Button variant="outline" className="h-11 justify-start" onClick={() => downloadIcs(booking, roomNames, lang)} data-testid="my-booking-ics"><CalendarPlus className="h-4 w-4" strokeWidth={1.5} /> {tx("Legg i kalender", "Add to calendar")}</Button>
                  <Button variant="outline" className="h-11 justify-start" onClick={() => window.print()} data-testid="my-booking-print"><Printer className="h-4 w-4" strokeWidth={1.5} /> {tx("Skriv ut", "Print")}</Button>
                  {booking.can_cancel ? (
                    <Button variant="ghost" className="h-11 justify-start text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setConfirmOpen(true)} data-testid="my-booking-cancel-button"><XCircle className="h-4 w-4" strokeWidth={1.5} /> {tx("Avbestill opphaldet", "Cancel the stay")}</Button>
                  ) : null}
                </div>
                <p className="mt-5 text-[0.8125rem] leading-relaxed text-muted-foreground">
                  {booking.can_cancel
                    ? booking.can_cancel_free
                      ? tx(`Gratis avbestilling fram til ${booking.cancel_deadline_display?.nn}.`, `Free cancellation until ${booking.cancel_deadline_display?.en}.`)
                      : tx("Fristen for gratis avbestilling er ute. Avbestiller du no, vert opphaldet fakturert.", "The free cancellation deadline has passed. Cancelling now will be charged for the stay.")
                    : tx("Denne bestillinga kan ikkje endrast her.", "This booking can't be changed here.")}
                </p>
                <p className="mt-3 text-[0.8125rem] leading-relaxed text-muted-foreground">
                  {tx("Vil du endre datoar eller rom? ", "Want to change dates or rooms? ")}
                  <a href={PHONE.tel} className="inline-flex items-center gap-1 font-medium text-foreground tabular hover:underline"><Phone className="h-3 w-3" strokeWidth={1.5} /> {PHONE.display}</a> · <a href="mailto:booking@rosendal-fjordhotel.no" className="inline-flex items-center gap-1 font-medium text-foreground hover:underline"><Mail className="h-3 w-3" strokeWidth={1.5} /> {tx("e-post", "email")}</a>
                </p>
              </div>
            </aside>
          </div>
        )}

        {!booking && !lookup.isError && (
          <div className="mt-16 grid gap-12 border-t border-border pt-10 no-print lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <Kicker className="mb-4">{tx("Ny bestilling", "New booking")}</Kicker>
              <h2 className="text-[1.5rem] font-medium leading-snug tracking-[-0.015em] text-foreground sm:text-[1.75rem]">{tx("Har du ikkje bestilt enno?", "Haven't booked yet?")}</h2>
              <p className="mt-3 text-[0.9375rem] leading-relaxed text-muted-foreground">{tx("Vel datoane dine og sjå ledige rom. Frukost er inkludert i alle hotellrom.", "Choose your dates and see available rooms. Breakfast is included in all hotel rooms.")}</p>
              <Button asChild className="mt-6" data-testid="my-booking-book-cta"><Link to="/bestill">{tx("Sjå ledige rom", "See available rooms")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Link></Button>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <Kicker className="mb-4">{tx("Avbestillingsvilkår", "Cancellation terms")}</Kicker>
              <p className="text-[0.9375rem] leading-relaxed text-muted-foreground">{content?.settings?.cancellation?.[lang] || content?.settings?.cancellation?.nn}</p>
              <div className="mt-5"><Link to="/praktisk#vilkar" className="text-[0.9375rem] font-medium text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground" data-testid="my-booking-terms-link">{tx("Les bestillingsvilkåra", "Read the booking terms")}</Link></div>
            </div>
          </div>
        )}
      </Container>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="rounded-lg border-border bg-background" data-testid="my-booking-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[1.5rem] font-medium tracking-[-0.015em] text-foreground">{tx("Avbestille opphaldet?", "Cancel the stay?")}</AlertDialogTitle>
            <AlertDialogDescription className="text-[0.9375rem] leading-relaxed">
              {booking?.can_cancel_free
                ? tx("Du er innanfor fristen – avbestillinga er gratis. Me sender ei stadfesting på e-post.", "You're within the deadline – cancellation is free. We'll email a confirmation.")
                : tx("Fristen for gratis avbestilling er ute. Opphaldet vert fakturert i tråd med vilkåra. Ring oss gjerne først på 53 48 80 00.", "The free cancellation deadline has passed. The stay will be charged per the terms. Feel free to call us first on +47 53 48 80 00.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-md" data-testid="my-booking-cancel-dismiss">{tx("Behald bestillinga", "Keep booking")}</AlertDialogCancel>
            <AlertDialogAction className="rounded-md bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={(e) => { e.preventDefault(); cancel.mutate(); }} disabled={cancel.isPending} data-testid="my-booking-cancel-confirm">{cancel.isPending ? tx("Avbestiller …", "Cancelling …") : tx("Ja, avbestill", "Yes, cancel")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
